import {forwardRef, useId, useState, type ComponentPropsWithoutRef, type ReactNode} from 'react';
import {cn} from '../lib/utils';
import {FieldLabel, FieldMessage} from '../lib/field';
import type {Testable} from '../lib/testable';
import {IconButton} from '../IconButton';
import {CloseIcon, EyeIcon, EyeOffIcon, SpinnerIcon} from '../icons';

export interface InputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'className'>, Testable {
	label?: ReactNode;
	error?: ReactNode;
	hint?: ReactNode;
	required?: boolean;
	icon?: ReactNode;
	isLoading?: boolean;
	onClear?: () => void;
	className?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
	label,
	error,
	hint,
	required = false,
	icon,
	isLoading = false,
	disabled = false,
	onClear,
	id,
	type,
	testId,
	className,
	...props
}, ref) => {
	const generatedId = useId();
	const [revealed, setRevealed] = useState(false);
	const inputId = id ?? generatedId;
	const messageId = `${inputId}-message`;
	const message = error ?? hint;
	const isPassword = type === 'password';
	const adornment = isLoading ? <SpinnerIcon className='animate-spin'/> : icon;
	const border = disabled ? 'border-background-500'
		: error ? 'border-tone-red bg-background-700'
			: 'border-foreground-400 hover:border-foreground-300 has-[:focus-visible]:border-foreground-300';

	return (
		<div data-component='InputField' className={cn(className, 'flex flex-col')}>
			{label &&
				<label htmlFor={inputId} className='mb-1.5 block w-fit'>
					<FieldLabel required={required} disabled={disabled}>{label}</FieldLabel>
				</label>}
			<div
				className={cn(controlStyles, border)}
				onMouseDown={event => {
					if (event.target !== event.currentTarget) return;
					event.preventDefault();
					event.currentTarget.querySelector('input')?.focus();
				}}>
				<input
					ref={ref}
					id={inputId}
					data-component='Input'
					data-testid={testId}
					type={isPassword && revealed ? 'text' : type}
					disabled={disabled}
					autoComplete='off'
					aria-required={required || undefined}
					aria-invalid={error ? true : undefined}
					aria-busy={isLoading || undefined}
					aria-describedby={message ? messageId : undefined}
					className={inputStyles}
					{...props}/>
				{onClear &&
					<IconButton
						size='sm'
						aria-label='Clear'
						icon={<CloseIcon/>}
						disabled={disabled || props.readOnly}
						onClick={onClear}/>}
				{isPassword &&
					<IconButton
						size='sm'
						aria-label={revealed ? 'Hide password' : 'Show password'}
						aria-pressed={revealed}
						icon={revealed ? <EyeOffIcon/> : <EyeIcon/>}
						disabled={disabled}
						onClick={() => setRevealed(on => !on)}/>}
				{adornment &&
					<span
						aria-hidden
						className={cn(adornmentStyles, disabled && 'text-foreground-300')}>
						{adornment}
					</span>}
			</div>
			{message && <FieldMessage id={messageId} error={Boolean(error)}>{message}</FieldMessage>}
		</div>
	);
});

Input.displayName = 'Input';

const controlStyles =
	'flex h-8 cursor-text items-center gap-2 rounded-md border px-2 transition-colors ' +
	'has-[input:disabled]:cursor-not-allowed';

const inputStyles =
	'min-w-0 flex-1 bg-transparent text-sm text-foreground-contrast outline-none ' +
	'placeholder:text-foreground-300 [&::-ms-reveal]:hidden ' +
	'disabled:cursor-not-allowed disabled:text-foreground-300';

const adornmentStyles = 'pointer-events-none shrink-0 text-foreground-200 [&_svg]:size-4';
